import { type FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, KeyRound, Plus, Webhook as WebhookIcon } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Input,
  Label,
  PageHeader,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@beamx/shared';
import { WEBHOOK_EVENTS, webhooks } from '../lib/api/webhooks.js';

export function WebhooksPage() {
  const qc = useQueryClient();
  const [showRegister, setShowRegister] = useState(false);
  const [revealed, setRevealed] = useState<{ title: string; secret: string } | null>(null);

  const list = useQuery({ queryKey: ['webhooks'], queryFn: () => webhooks.list() });

  const rotate = useMutation({
    mutationFn: (id: string) => webhooks.rotateSecret(id),
    onSuccess: (res) => {
      setRevealed({ title: 'New signing secret', secret: res.newSigningSecret });
      void qc.invalidateQueries({ queryKey: ['webhooks'] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to rotate secret'),
  });

  return (
    <>
      <PageHeader
        title="Webhooks"
        description="Receive HMAC-signed event callbacks for delivery, bounces, opens, and clicks."
        actions={
          <Button onClick={() => setShowRegister(true)}>
            <Plus className="h-4 w-4" />
            Register webhook
          </Button>
        }
      />

      <Card>
        <CardContent className="p-0">
          {list.isLoading ? (
            <div className="space-y-3 p-6">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : list.data && list.data.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>URL</TableHead>
                  <TableHead>Events</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.data.map((w) => (
                  <TableRow key={w.id}>
                    <TableCell className="font-medium text-foreground">{w.name}</TableCell>
                    <TableCell className="max-w-xs truncate font-mono text-xs text-muted-foreground" title={w.url}>{w.url}</TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground">{w.subscribedEvents.length} event(s)</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={w.isActive ? 'success' : 'muted'} dot>{w.isActive ? 'Active' : 'Disabled'}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" loading={rotate.isPending && rotate.variables === w.id} onClick={() => rotate.mutate(w.id)}>
                        <KeyRound className="h-4 w-4" />
                        Rotate secret
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="p-6">
              <EmptyState
                icon={WebhookIcon}
                title="No webhooks"
                description="Register an endpoint to start receiving event callbacks."
                action={<Button onClick={() => setShowRegister(true)}><Plus className="h-4 w-4" />Register webhook</Button>}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <RegisterDialog
        open={showRegister}
        onOpenChange={setShowRegister}
        onRegistered={(secret) => setRevealed({ title: 'Signing secret', secret })}
      />
      <SecretDialog reveal={revealed} onClose={() => setRevealed(null)} />
    </>
  );
}

function RegisterDialog({
  open,
  onOpenChange,
  onRegistered,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onRegistered: (secret: string) => void;
}) {
  const qc = useQueryClient();
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [events, setEvents] = useState<Set<string>>(new Set(['Delivered', 'HardBounce']));

  function toggle(ev: string) {
    setEvents((prev) => {
      const next = new Set(prev);
      if (next.has(ev)) next.delete(ev);
      else next.add(ev);
      return next;
    });
  }

  const register = useMutation({
    mutationFn: () => webhooks.register({ url: url.trim(), name: name.trim(), events: Array.from(events) }),
    onSuccess: (res) => {
      toast.success('Webhook registered');
      void qc.invalidateQueries({ queryKey: ['webhooks'] });
      onOpenChange(false);
      setUrl('');
      setName('');
      setEvents(new Set(['Delivered', 'HardBounce']));
      onRegistered(res.signingSecret);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to register webhook'),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (events.size === 0) {
      toast.error('Select at least one event.');
      return;
    }
    register.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Register webhook</DialogTitle>
          <DialogDescription>We POST signed JSON to this URL for each subscribed event.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="w-name">Name</Label>
            <Input id="w-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Production listener" required autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="w-url">Endpoint URL</Label>
            <Input id="w-url" type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://api.acme.example/beamx/webhook" required />
          </div>
          <div className="space-y-1.5">
            <Label>Events ({events.size} selected)</Label>
            <div className="grid grid-cols-2 gap-1.5 rounded-md border p-3 text-sm">
              {WEBHOOK_EVENTS.map((ev) => (
                <label key={ev} className="flex cursor-pointer items-center gap-2">
                  <input type="checkbox" checked={events.has(ev)} onChange={() => toggle(ev)} className="rounded border-input accent-primary" />
                  <span>{ev}</span>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={register.isPending}>Cancel</Button>
            <Button type="submit" loading={register.isPending}>Register</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SecretDialog({ reveal, onClose }: { reveal: { title: string; secret: string } | null; onClose: () => void }) {
  return (
    <Dialog open={!!reveal} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        {reveal && (
          <>
            <DialogHeader>
              <DialogTitle>{reveal.title}</DialogTitle>
              <DialogDescription className="text-destructive">
                Shown once. Store it now — verify inbound callbacks with this secret.
              </DialogDescription>
            </DialogHeader>
            <code className="block break-all rounded-lg bg-slate-950 p-3 font-mono text-xs text-emerald-300">{reveal.secret}</code>
            <DialogFooter>
              <Button variant="ghost" onClick={() => { void navigator.clipboard.writeText(reveal.secret); toast.success('Copied'); }}>
                <Copy className="h-4 w-4" />
                Copy
              </Button>
              <Button onClick={onClose}>Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
